import React from 'react';
import { Trans } from 'react-i18next';
import { TFunction } from 'i18next';
import { Notification } from '../stores/notificationStore';
import { useQuoteStore } from '../stores/quoteStore';
import { jobSearchPath } from './jobSearchRouting';

export interface NotificationMessageModalProps {
  title: string;
  subTitle?: string | null | React.ReactNode;
  message: string;
  primaryButton?: {
    label: string;
    href?: string;
    onClick?: () => void;
    state?: { selected_id?: string | number } & Record<string, unknown>;
  };
  onClose?: () => void;
}

// Upper bound on how long we wait for the "does a quote already exist for
// this job" lookup before giving up and falling back to the job search page.
// Without this, a hung RPC would leave the action button's loading spinner
// spinning forever with no way for the user to navigate anywhere.
const QUOTE_LOOKUP_TIMEOUT_MS = 5000;

const withTimeout = <T,>(promise: Promise<T>, timeoutMs: number): Promise<T | undefined> =>
  Promise.race([
    promise,
    new Promise<undefined>(resolve => setTimeout(() => resolve(undefined), timeoutMs)),
  ]);

export const getNotificationMessage = (notification: Notification, t: TFunction): string => {
  const senderDisplayName =
    typeof notification.payload?.sender_display_name === 'string'
      ? notification.payload.sender_display_name
      : '';
  const name =
    senderDisplayName || notification.source_profile?.first_name || t('first_name_fallback');
  const project = notification.payload?.project_name || '';
  const amount = notification.payload?.amount as number | undefined;
  const jobTitle = notification.payload?.job_title || '';
  const hasApplicants = notification.payload?.has_applicants === true;
  const messageKey =
    notification.type === 'job_expired' && hasApplicants
      ? 'job_expired_hasApplicants'
      : notification.type;
  return t(`notifications:message.${messageKey}`, {
    name,
    project,
    amount,
    jobTitle,
    defaultValue: (notification.payload?.message as string) || t('notifications:message.fallback'),
  });
};

export const getActionButton = (
  notification: Notification,
  t: TFunction,
  onOpenNotificationMessageModal: (notificationMessageModal: NotificationMessageModalProps) => void,
  approveServiceCategory?: (service_id: number) => Promise<void>,
  approveIndustry?: (industry_id: number) => Promise<void>,
  goToEntity?: (payload: Record<string, unknown>) => void
) => {
  let label = t('button.viewDetails', { ns: 'notifications' });
  let onClick: (() => void | Promise<void>) | undefined = undefined;

  // Build hub URL for a project, optionally opening a specific quote
  const projectQuoteHref = (jobId: string, quoteId?: string | null) =>
    quoteId && quoteId.trim() !== ''
      ? `/projects/${jobId}?selectedQuoteId=${quoteId}`
      : `/projects/${jobId}`;

  // Helper to open generic modal for cases that were previously direct redirects
  const openGenericModal = (
    buttonLabel: string,
    buttonHref: string,
    buttonState?: { selected_id?: string | number } & Record<string, unknown>
  ) => {
    onOpenNotificationMessageModal({
      title: notification.source_profile?.first_name || t('notification', { ns: 'common' }),
      message: getNotificationMessage(notification, t),
      primaryButton: {
        label: buttonLabel,
        href: buttonHref,
        state: buttonState,
      },
    });
  };

  switch (notification.type) {
    case 'credits_gifted_manual':
    case 'credits_gifted_monthly':
      label = t('button.manageWallet', { ns: 'notifications' });
      onClick = () =>
        openGenericModal(t('button.manageWallet', { ns: 'notifications' }), '/dashboard');
      break;

    case 'alliance_accepted':
    case 'alliance_created':
    case 'alliance_retired':
    case 'alliance_re_requested':
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        openGenericModal(t('button.manageAlliances', { ns: 'notifications' }), '/alliances');
      break;

    case 'alliance_rejected':
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        onOpenNotificationMessageModal({
          title: t('alliance_rejected_modal.title', {
            name: notification.source_profile?.first_name,
            ns: 'notifications',
          }),
          subTitle: (
            <Trans
              i18nKey="alliance_rejected_modal.subTitle"
              ns="notifications"
              values={{ name: notification.source_profile?.first_name }}
              components={{ bold: <span className="font-bold" /> }}
            />
          ),
          message: notification.payload?.reject_reason as string,
          primaryButton: {
            label: t('button.manageAlliances', { ns: 'notifications' }),
            href: '/alliances',
          },
        });
      break;

    case 'quote_request_received': {
      const quoteRequestId =
        (notification.payload?.quote_request_id as string) ||
        (notification.payload?.job_id as string) ||
        '';
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        openGenericModal(t('button.viewQuoteRequest', { ns: 'notifications' }), '/prospects', {
          selected_id: quoteRequestId,
        });
      break;
    }

    case 'quote_change_requested': {
      const changeRequestJobId = (notification.payload?.job_id as string) || '';
      const changeRequestQuoteId =
        (notification.payload?.quote_id as string) ||
        (notification.payload?.parent_quote_id as string) ||
        '';
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        onOpenNotificationMessageModal({
          title: t('quote_request_change_modal.title', {
            name: notification.source_profile?.first_name,
            ns: 'notifications',
          }),
          subTitle: (
            <Trans
              i18nKey="quote_request_change_modal.subTitle"
              ns="notifications"
              values={{ name: notification.source_profile?.first_name }}
              components={{ bold: <span className="font-bold" /> }}
            />
          ),
          message: notification.payload?.message as string,
          primaryButton: {
            label: t('button.viewSubmission', { ns: 'notifications' }),
            href: changeRequestJobId
              ? projectQuoteHref(changeRequestJobId, changeRequestQuoteId)
              : '/quotes',
            ...(changeRequestJobId
              ? {}
              : {
                  state: { selected_id: changeRequestQuoteId },
                }),
          },
        });
      break;
    }

    case 'adjustment_submitted': {
      const adjustmentJobId = (notification.payload?.job_id as string) || '';
      const adjustmentQuoteId =
        (notification.payload?.quote_id as string) ||
        (notification.payload?.parent_quote_id as string) ||
        '';
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        onOpenNotificationMessageModal({
          title: t('adjustment_submitted_modal.title', {
            name: notification.source_profile?.first_name,
            ns: 'notifications',
          }),
          subTitle: (
            <Trans
              i18nKey="adjustment_submitted_modal.subTitle"
              ns="notifications"
              values={{ name: notification.source_profile?.first_name }}
              components={{ bold: <span className="font-bold" /> }}
            />
          ),
          message: notification.payload?.message as string,
          primaryButton: {
            label: t('button.viewAdjustment', { ns: 'notifications' }),
            href: adjustmentJobId
              ? projectQuoteHref(adjustmentJobId, adjustmentQuoteId)
              : '/projects',
            ...(adjustmentJobId
              ? {}
              : {
                  state: { selected_id: adjustmentQuoteId },
                }),
          },
        });
      break;
    }

    case 'quote_request_accepted': {
      const jobId = (notification.payload?.job_id as string) || '';
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        openGenericModal(t('button.viewJob', { ns: 'notifications' }), `/projects/${jobId}`);
      break;
    }

    case 'job_cancelled': {
      const jobId = (notification.payload?.job_id as string) || '';
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        openGenericModal(t('button.viewJob', { ns: 'notifications' }), `/projects/${jobId}`);
      break;
    }

    // Deliberately not joined with the job_edited_by_admin/job_unpublished_by_admin/
    // job_paused_by_admin group below: that group renders payload.reason in the modal
    // body, but this notification's payload has no `reason` key, which would show an
    // empty message.
    case 'job_assigned_by_admin': {
      const jobId = (notification.payload?.job_id as string) || '';
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        openGenericModal(t('button.viewJob', { ns: 'notifications' }), `/projects/${jobId}`);
      break;
    }

    case 'job_edited_by_admin':
    case 'job_unpublished_by_admin':
    case 'job_paused_by_admin': {
      const jobId = (notification.payload?.job_id as string) || '';
      const reason = (notification.payload?.reason as string) || '';
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        onOpenNotificationMessageModal({
          title: getNotificationMessage(notification, t),
          subTitle: t('job_moderated_by_admin_modal.subTitle', { ns: 'notifications' }),
          message: reason,
          primaryButton: {
            label: t('button.viewJob', { ns: 'notifications' }),
            href: jobId ? `/projects/${jobId}` : '/jobs',
          },
        });
      break;
    }

    case 'new_job_posted': {
      const newJobId = (notification.payload?.job_id as string) || '';
      label = t('button.view', { ns: 'notifications' });
      onClick = async () => {
        // The freelancer may not have submitted a quote yet, so there's no
        // guarantee a project/hub exists for this job. Only send them there
        // if they already have a quote; otherwise fall back to the job
        // search page, where the job can still be found and applied to.
        const existingQuoteId = newJobId
          ? await withTimeout(
              useQuoteStore.getState().fetchQuoteIdByJobId(newJobId),
              QUOTE_LOOKUP_TIMEOUT_MS
            )
          : undefined;
        if (newJobId && existingQuoteId) {
          openGenericModal(
            t('button.viewJob', { ns: 'notifications' }),
            projectQuoteHref(newJobId, existingQuoteId)
          );
        } else {
          openGenericModal(
            t('button.viewJob', { ns: 'notifications' }),
            newJobId ? jobSearchPath(newJobId) : '/job-search'
          );
        }
      };
      break;
    }

    case 'quote_request_rejected': {
      const jobId = (notification.payload?.job_id as string) || '';
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        onOpenNotificationMessageModal({
          title: t('quote_request_rejected_modal.title', {
            name: notification.source_profile?.first_name,
            ns: 'notifications',
          }),
          subTitle: (
            <Trans
              i18nKey="quote_request_rejected_modal.subTitle"
              ns="notifications"
              values={{ name: notification.source_profile?.first_name }}
              components={{ bold: <span className="font-bold" /> }}
            />
          ),
          message: notification.payload?.reject_reason as string,
          primaryButton: {
            label: t('button.viewJob', { ns: 'notifications' }),
            href: `/projects/${jobId}`,
          },
        });
      break;
    }

    case 'quote_alliance_invited':
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        openGenericModal(t('button.viewQuoteRequest', { ns: 'notifications' }), '/prospects', {
          selected_id: (notification.payload?.job_id as string) || '',
        });
      break;

    case 'quote_alliance_accepted':
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        openGenericModal(t('button.viewSubmission', { ns: 'notifications' }), '/quotes', {
          selected_id: (notification.payload?.quote_id as string) || '',
        });
      break;

    case 'quote_alliance_rejected':
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        onOpenNotificationMessageModal({
          title: t('quote_alliance_rejected_modal.title', {
            name: notification.source_profile?.first_name,
            ns: 'notifications',
          }),
          subTitle: (
            <Trans
              i18nKey="quote_alliance_rejected_modal.subTitle"
              ns="notifications"
              values={{ name: notification.source_profile?.first_name }}
              components={{ bold: <span className="font-bold" /> }}
            />
          ),
          message: notification.payload?.reject_reason as string,
          primaryButton: {
            label: t('button.viewSubmission', { ns: 'notifications' }),
            href: '/quotes',
            state: {
              selected_id: (notification.payload?.quote_id as string) || '',
            },
          },
        });
      break;

    case 'quote_alliance_retired':
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        openGenericModal(t('button.viewSubmission', { ns: 'notifications' }), '/quotes', {
          selected_id: (notification.payload?.quote_id as string) || '',
        });
      break;

    case 'quote_submitted': {
      const submittedJobId = (notification.payload?.job_id as string) || '';
      const submittedQuoteId =
        (notification.payload?.quote_id as string) ||
        (notification.payload?.parent_quote_id as string) ||
        '';
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        openGenericModal(
          t('button.viewSubmission', { ns: 'notifications' }),
          submittedJobId ? projectQuoteHref(submittedJobId, submittedQuoteId) : '/projects',
          submittedJobId ? undefined : { selected_id: submittedQuoteId }
        );
      break;
    }

    case 'child_quote_submitted':
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        openGenericModal(t('button.viewSubmission', { ns: 'notifications' }), '/quotes', {
          selected_id: (notification.payload?.parent_quote_id as string) || '',
        });
      break;

    case 'parent_quote_submitted':
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        openGenericModal(t('button.viewSubmission', { ns: 'notifications' }), '/quotes', {
          selected_id: (notification.payload?.quote_id as string) || '',
        });
      break;

    case 'quote_accepted':
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        openGenericModal(t('button.viewProject', { ns: 'notifications' }), '/projects', {
          selected_id: (notification.payload?.quote_id as string) || '',
        });
      break;

    case 'parent_quote_accepted':
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        openGenericModal(t('button.viewProject', { ns: 'notifications' }), '/projects', {
          selected_id: (notification.payload?.parent_quote_id as string) || '',
        });
      break;

    case 'quote_rejected':
    case 'parent_quote_rejected':
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        onOpenNotificationMessageModal({
          title: t('quote_rejected_modal.title', {
            name: notification.source_profile?.first_name,
            ns: 'notifications',
          }),
          subTitle: (
            <Trans
              i18nKey="quote_rejected_modal.subTitle"
              ns="notifications"
              values={{ name: notification.source_profile?.first_name }}
              components={{ bold: <span className="font-bold" /> }}
            />
          ),
          message:
            (notification.payload?.reject_reason as string) ||
            t('quote_rejected_modal.default_message', { ns: 'notifications' }),
          primaryButton: {
            label: t('button.viewSubmission', { ns: 'notifications' }),
            href: '/quotes',
            state: {
              selected_id: (notification.payload?.quote_id as string) || '',
            },
          },
        });
      break;

    case 'quote_not_accepted':
    case 'parent_quote_not_accepted':
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        openGenericModal(t('button.viewSubmission', { ns: 'notifications' }), '/quotes', {
          selected_id: (notification.payload?.quote_id as string) || '',
        });
      break;

    case 'project_status_updated': {
      const projectStatusJobId = (notification.payload?.job_id as string) || '';
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        openGenericModal(
          t('button.viewProject', { ns: 'notifications' }),
          `/projects/${projectStatusJobId}`
        );
      break;
    }

    case 'hub_message':
      label = t('button.viewHub', { ns: 'notifications' });
      onClick = () => {
        const jobId = notification.payload?.job_id as string;
        const quoteId = notification.payload?.quote_id as string | undefined;
        const hubPath = quoteId
          ? `/projects/${jobId}?selectedQuoteId=${quoteId}`
          : `/projects/${jobId}`;
        openGenericModal(t('button.viewHub', { ns: 'notifications' }), hubPath);
      };
      break;

    case 'credits_added':
      label = t('button.view', { ns: 'notifications' });
      onClick = () => openGenericModal(t('button.manageWallet', { ns: 'notifications' }), '');

      break;

    case 'new_service_category_suggested':
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        onOpenNotificationMessageModal({
          title: t('new_service_category_suggested_modal.title', {
            name: notification.source_profile?.first_name,
            ns: 'notifications',
          }),
          subTitle: (
            <Trans
              i18nKey="new_service_category_suggested_modal.subTitle"
              ns="notifications"
              values={{ name: notification.source_profile?.first_name }}
              components={{ bold: <span className="font-bold" /> }}
            />
          ),
          message: notification.payload?.category_label as string,
          primaryButton: {
            label: t('button.approveAndMakeVisibleForAll', { ns: 'notifications' }),
            onClick: () => approveServiceCategory?.(notification.payload?.category_id as number),
          },
        });
      break;

    case 'new_industry_suggested':
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        onOpenNotificationMessageModal({
          title: t('new_industry_suggested_modal.title', {
            name: notification.source_profile?.first_name,
          }),
          subTitle: (
            <Trans
              i18nKey="new_industry_suggested_modal.subTitle"
              ns="notifications"
              values={{ name: notification.source_profile?.first_name }}
              components={{ bold: <span className="font-bold" /> }}
            />
          ),
          message: notification.payload?.industry_label as string,
          primaryButton: {
            label: t('button.approveAndMakeVisibleForAll', { ns: 'notifications' }),
            onClick: () => approveIndustry?.(notification.payload?.industry_id as number),
          },
        });
      break;

    case 'new_contact_added':
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        openGenericModal(t('button.viewProspects', { ns: 'notifications' }), '/prospects', {
          selected_id: (notification.payload?.recruiter_id as string) || '',
        });
      break;

    case 'project_cancelled':
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        openGenericModal(t('button.viewProject', { ns: 'notifications' }), '/projects', {
          selected_id: (notification.payload?.project_id as string) || '',
        });
      break;

    case 'project_completed':
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        openGenericModal(t('button.viewProject', { ns: 'notifications' }), '/projects', {
          selected_id: (notification.payload?.project_id as string) || '',
        });
      break;

    case 'job_expiring_soon':
    case 'job_expired':
    case 'job_removal_warning':
    case 'job_removed':
    case 'job_expired_for_applicant':
    case 'job_renewed_for_applicant':
    case 'job_selection_period_started':
    case 'job_selection_period_started_for_applicant': {
      const jobId = (notification.payload?.job_id as string) || '';
      label = t('button.viewProject', { ns: 'notifications' });
      onClick = () =>
        openGenericModal(t('button.viewProject', { ns: 'notifications' }), `/projects/${jobId}`);
      break;
    }

    case 'new_report_submitted': {
      const reportContentType = notification.payload?.content_type as string | undefined;
      const canNavigateToReportEntity = ['profile', 'review', 'job'].includes(
        reportContentType ?? ''
      );
      label = t('button.view', { ns: 'notifications' });
      onClick = () =>
        onOpenNotificationMessageModal({
          title: t('new_report_submitted_modal.title', {
            name:
              notification.source_profile?.first_name ||
              t('first_name_fallback', { ns: 'notifications' }),
            ns: 'notifications',
          }),
          subTitle: (
            <Trans
              i18nKey="new_report_submitted_modal.subTitle"
              ns="notifications"
              values={{
                name:
                  notification.source_profile?.first_name ||
                  t('first_name_fallback', { ns: 'notifications' }),
              }}
              components={{ bold: <span className="font-bold" /> }}
            />
          ),
          message: t(`report_reasons.${notification.payload?.reason}`, {
            defaultValue: notification.payload?.reason,
            ns: 'reports',
          }) as string,
          ...(canNavigateToReportEntity && {
            primaryButton: {
              label: t('button.viewDetails', { ns: 'notifications' }),
              onClick: () => goToEntity?.(notification.payload),
            },
          }),
        });
      break;
    }

    default:
      label = t('button.viewDetails', { ns: 'notifications' });
  }

  return {
    onClick,
    label,
  };
};

export const NOTIFICATION_REFRESH_INTERVAL = 60_000;
